<template>
  <s-field
    id="showGradeIncrement"
    :error="errors?.['gradeIncrement']"
    :label="t('gradingPrecision')"
  >
    <template #tooltip>
      <p>{{ t('gradingPrecisionTooltip') }}</p>
      <p>{{ t('gradingPrecisionExample') }}</p>
    </template>
    <div class="flex flex-col sm:flex-row sm:items-center gap-5 mb-4">
      <div class="w-1/4 min-w-[13rem]" aria-hidden="true">
        <s-input-field
          id="gradeIncrement"
          :prefix="t('increment')"
          suffix="%"
          :model-value="formatToGradePrecision(currentGradeIncrement)"
          disabled
        />
      </div>
      <div class="flex-1 grid grid-cols-2 gap-1">
        <input
          :aria-labelledby="`${sliderMinLabelId} ${sliderMaxLabelId}`"
          type="range"
          min="0"
          max="7"
          :value="7 - precisionLevel"
          :aria-valuetext="
            t('gradingPrecisionValue', formatToGradePrecision(currentGradeIncrement))
          "
          @input="handleSliderInput"
          class="col-span-2 h-3 bg-gray-300 rounded-lg appearance-none cursor-pointer flex-1"
        />
        <label :id="sliderMinLabelId" class="text-sm text-gray-600">
          <span class="sr-only">{{ t('minLabel') }}</span>
          {{ t('finestPrecision') }}
          <span class="sr-only">,</span>
        </label>
        <label :id="sliderMaxLabelId" class="justify-self-end text-sm text-gray-600">
          <span class="sr-only">{{ t('maxLabel') }}</span>
          {{ t('binary') }}
        </label>
      </div>
    </div>
    <grade-increment-ticks :weight="weight" :grade-increment="currentGradeIncrement" />
  </s-field>
</template>

<script setup lang="ts">
import {defineEmits, defineProps, onMounted, ref, watch, useId} from 'vue';
import SField from '../../../design-system/SField.vue';
import {useVModel} from '@vueuse/core';
import GradeIncrementTicks from './GradeIncrementTicks.vue';
import {
  findLevelIndexByIncrement,
  gradeIncrementLevels,
} from '../../../composables/useGradeIncrementLevels';
import SInputField from '../../../design-system/SInputField.vue';
import {useI18n} from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: Number,
    default: 0.05,
  },
  weight: {
    type: Number,
    required: true,
    default: 1,
  },
  errors: Object,
});

const {t} = useI18n({
  useScope: 'local',
  inheritLocale: true,
});

const emit = defineEmits(['update:modelValue']);
const gradeIncrement = useVModel(props, 'modelValue', emit);
const sliderMinLabelId = useId();
const sliderMaxLabelId = useId();

const DEFAULT_GRADE_INCREMENT = 0.05;
const precisionLevel = ref(
  findLevelIndexByIncrement(gradeIncrement.value || DEFAULT_GRADE_INCREMENT)
);
const currentGradeIncrement = ref(gradeIncrementLevels[precisionLevel.value].gradeIncrement);

// Sync when slider changes
watch(precisionLevel, (idx) => {
  currentGradeIncrement.value = gradeIncrementLevels[idx].gradeIncrement;
  gradeIncrement.value = currentGradeIncrement.value;
});

// Keep slider in sync if parent changes modelValue directly
watch(gradeIncrement, (val) => {
  const idx = findLevelIndexByIncrement(val);
  precisionLevel.value = idx;
  currentGradeIncrement.value = gradeIncrementLevels[idx].gradeIncrement;
});

// Keep slider position up-to-date on mount
onMounted(() => {
  precisionLevel.value = findLevelIndexByIncrement(gradeIncrement.value || DEFAULT_GRADE_INCREMENT);
  currentGradeIncrement.value = gradeIncrementLevels[precisionLevel.value].gradeIncrement;
});

function formatToGradePrecision(gradeIncrement: number) {
  const decimals = [0.333, 0.125].includes(Number(gradeIncrement.toFixed(3))) ? 3 : 2;
  return Number((gradeIncrement * 100).toFixed(decimals));
}

function handleSliderInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const invertedValue = parseInt(target.value);
  precisionLevel.value = 7 - invertedValue;
}
</script>
<i18n>
{
  "en": {
    "gradingPrecision": "Grading Precision",
    "gradingPrecisionTooltip": "The smallest increment that can be used to grade the task.",
    "gradingPrecisionExample": "Ex) 50% grading increments mean only zero, half, and full marks can be awarded.",
    "increment": "Increment",
    "finestPrecision": "Finest Precision",
    "binary": "Binary",
    "minLabel": "Min:",
    "maxLabel": "Max:",
    "gradingPrecisionValue": "Grading precision: {count}%"
  },
  "fr": {
    "gradingPrecision": "Précision de notation",
    "gradingPrecisionTooltip": "Le plus petit incrément qui peut être utilisé pour noter la tâche.",
    "gradingPrecisionExample": "Ex) Des incréments de notation de 50% signifient que seules les notes zéro, demi et complète peuvent être attribuées.",
    "increment": "Incrément",
    "finestPrecision": "Précision maximale",
    "binary": "Binaire",
    "minLabel": "Min :",
    "maxLabel": "Max :",
    "gradingPrecisionValue": "Précision de notation : {count}%"
  }
}
</i18n>
